import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  STORAGE_KEY, IMP_KEY, readStored, writeStored, clearStored, bumpEpoch, currentEpoch,
  tokenExpiresAt, classifyResponse, fetchWithTimeout, rotateSession, backoffFor,
} from '../lib/auth-session';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};

/**
 * Robust body reader. Some environments (PWA service workers, fetch
 * interceptors, Playwright CDP capture) consume the response body before
 * we get a chance to read it, which makes `await res.text()` throw
 * "body stream already read" — most painfully on error responses where the
 * user really needs to see the detail message.
 */
const readBody = async (res) => {
  try { return await res.clone().text(); } catch (e1) {
    try { return await res.text(); } catch (e2) {
      try { const buf = await res.arrayBuffer(); return new TextDecoder('utf-8').decode(buf); } catch (e3) {
        console.warn('[auth] readBody failed', { e1, e2, e3 });
        return '';
      }
    }
  }
};

const STATUS_MESSAGES = {
  400: 'That sign-in link is invalid or expired. Request a new one.',
  401: 'Sign-in expired. Please sign in again.',
  403: 'You don\u2019t have access to this account.',
  404: 'Account not found.',
  429: 'Too many attempts. Please try again in a minute.',
  500: 'Something went wrong on our end. Please try again.',
};
const extractError = async (res, defaultMsg) => {
  const body = await readBody(res);
  if (body) {
    try { const j = JSON.parse(body); if (j?.detail) return String(j.detail); } catch { /* not JSON */ }
    if (body.length < 300) return body;
  }
  return STATUS_MESSAGES[res.status] || `${defaultMsg} (HTTP ${res.status})`;
};

const postJson = async (path, body, token) => fetch(`${API}${path}`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

/**
 * Wraps the app with auth state. Credentials live in localStorage (shared by
 * every tab); the short-lived access token is rotated through a cross-tab
 * lock so tabs never race each other with the same refresh token.
 *
 *  authStatus: 'validating' → 'ready' | 'reconnecting'
 *    reconnecting = we hold credentials but the server could not confirm them
 *    (offline / 5xx / 429). Credentials are preserved and re-validated with
 *    bounded backoff; access is NOT granted until the server confirms.
 *  connectionIssue: a mid-session refresh is failing transiently (banner).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(null);
  const [authStatus, setAuthStatus] = useState('validating');
  const [connectionIssue, setConnectionIssue] = useState(false);

  const refreshInFlight = useRef(null);
  const retryRef = useRef({ attempt: 0, timer: null });
  const refreshRef = useRef(() => Promise.resolve(null));
  const bootRetryRef = useRef(null);

  const clearRetry = () => {
    const s = retryRef.current;
    if (s.timer) clearTimeout(s.timer);
    retryRef.current = { attempt: 0, timer: null };
  };

  const hardSignOut = useCallback(() => {
    bumpEpoch();
    clearRetry();
    clearStored();
    setUser(null);
    setToken(null);
    setImpersonation(null);
    setConnectionIssue(false);
    setAuthStatus('ready');
    setLoading(false);
  }, []);

  // Persist a freshly issued session (any login flow). Legacy / impersonation
  // responses carry no refresh_token → none is stored, so the keep-alive can
  // never silently swap the session back to another principal.
  const commitSession = useCallback((data, imp = null) => {
    bumpEpoch();
    clearRetry();
    const next = { token: data.token, user: data.user };
    if (data.refresh_token) next.refresh_token = data.refresh_token;
    writeStored(next);
    if (imp) localStorage.setItem(IMP_KEY, JSON.stringify(imp));
    else localStorage.removeItem(IMP_KEY);
    setToken(data.token);
    setUser(data.user);
    setImpersonation(imp);
    setConnectionIssue(false);
    setAuthStatus('ready');
    setLoading(false);
    return data;
  }, []);

  const scheduleRefreshRetry = useCallback(() => {
    const s = retryRef.current;
    if (s.timer) return;
    s.timer = setTimeout(() => {
      retryRef.current.timer = null;
      refreshRef.current();
    }, backoffFor(s.attempt));
    s.attempt += 1;
  }, []);

  // Single-flight (per tab) rotation; cross-tab coordination lives in rotateSession.
  // `staleToken` = the access token that just failed / is about to expire, so a
  // sibling tab's newer token is adopted instead of rotating a second time.
  // Resolves to the current access token, or null when no new token is available.
  const refreshSession = useCallback(async (staleToken = null) => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const startEpoch = currentEpoch();
    refreshInFlight.current = (async () => {
      try {
        const r = await rotateSession(API, startEpoch, { staleToken: staleToken || readStored()?.token || null });
        if (currentEpoch() !== startEpoch) return null;
        if (r.status === 'ok') {
          clearRetry();
          setConnectionIssue(false);
          setToken(r.token);
          return r.token;
        }
        if (r.status === 'rejected') { hardSignOut(); return null; }
        if (r.status === 'transient') { setConnectionIssue(true); scheduleRefreshRetry(); }
        return null;
      } finally { refreshInFlight.current = null; }
    })();
    return refreshInFlight.current;
  }, [hardSignOut, scheduleRefreshRetry]);
  refreshRef.current = refreshSession;

  // Bootstrap: hydrate from storage, then confirm with the server. Only a
  // definitive rejection (401 that a refresh cannot cure) clears credentials.
  useEffect(() => {
    const stored = readStored();
    try { setImpersonation(JSON.parse(localStorage.getItem(IMP_KEY) || 'null')); } catch { /* ignore */ }
    if (!stored?.token) { setLoading(false); setAuthStatus('ready'); return undefined; }
    setToken(stored.token);
    setUser(stored.user || null);
    const startEpoch = currentEpoch();
    let cancelled = false;
    let timer = null;

    const me = async (tk) => {
      try {
        const res = await fetchWithTimeout(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${tk}` } });
        return { res, kind: classifyResponse(res) };
      } catch { return { res: null, kind: 'transient' }; }
    };
    const validate = async () => {
      let tk = readStored()?.token;
      if (!tk) return { status: 'none' };
      let { res, kind } = await me(tk);
      if (kind === 'rejected' && res.status === 401) {
        const r = await rotateSession(API, startEpoch, { staleToken: tk });
        if (r.status === 'ok') { tk = r.token; ({ res, kind } = await me(tk)); }
        else if (r.status === 'transient') return { status: 'transient' };
        else if (r.status === 'stale') return { status: 'stale' };
        else return { status: 'rejected' };
      }
      if (kind === 'ok') return { status: 'ok', token: tk, user: await res.json() };
      return { status: kind };
    };
    const run = async (attempt) => {
      const out = await validate();
      if (cancelled || currentEpoch() !== startEpoch) return;
      if (out.status === 'ok') {
        const cur = readStored() || {};
        writeStored({ ...cur, token: out.token, user: out.user });
        setToken(out.token);
        setUser(out.user);
        if (out.user._impersonation) {
          setImpersonation(out.user._impersonation);
          localStorage.setItem(IMP_KEY, JSON.stringify(out.user._impersonation));
        } else {
          setImpersonation(null);
          localStorage.removeItem(IMP_KEY);
        }
        setAuthStatus('ready');
        setConnectionIssue(false);
        setLoading(false);
      } else if (out.status === 'transient') {
        setAuthStatus('reconnecting');
        setLoading(false);
        timer = setTimeout(() => run(attempt + 1), backoffFor(attempt));
        bootRetryRef.current = () => { if (timer) clearTimeout(timer); timer = null; run(attempt + 1); };
      } else if (out.status === 'stale') {
        setLoading(false);
      } else {
        hardSignOut();
        setLoading(false);
      }
    };
    run(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); bootRetryRef.current = null; };
  }, [hardSignOut]);

  // Cross-tab sync: another tab rotated, signed in (possibly as someone else) or signed out.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== STORAGE_KEY && e.key !== IMP_KEY) return;
      if (e.key === IMP_KEY) {
        try { setImpersonation(JSON.parse(localStorage.getItem(IMP_KEY) || 'null')); } catch { /* ignore */ }
        return;
      }
      const next = readStored();
      if (!next?.token) {
        bumpEpoch();
        clearRetry();
        setUser(null); setToken(null); setImpersonation(null);
        setConnectionIssue(false); setAuthStatus('ready'); setLoading(false);
        return;
      }
      setUser((prev) => {
        if (prev?.id !== next.user?.id) { bumpEpoch(); clearRetry(); return next.user || null; }
        return prev;
      });
      setToken((prev) => (prev === next.token ? prev : next.token));
      setAuthStatus('ready');
      setConnectionIssue(false);
      setLoading(false);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Proactive rotation shortly before the access token expires (+ on tab wake).
  useEffect(() => {
    if (!token || !readStored()?.refresh_token) return undefined;
    const exp = tokenExpiresAt(token);
    const delay = exp
      ? Math.min(24 * 60 * 60 * 1000, Math.max(5000, exp - Date.now() - 2 * 60 * 1000))
      : 10 * 60 * 1000;
    const id = setTimeout(() => { refreshRef.current(token); }, delay);
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      const e = tokenExpiresAt(token);
      if (e && e - Date.now() < 60 * 1000) refreshRef.current(token);
    };
    document.addEventListener('visibilitychange', onWake);
    return () => { clearTimeout(id); document.removeEventListener('visibilitychange', onWake); };
  }, [token]);

  const retryConnection = useCallback(() => {
    if (bootRetryRef.current) { bootRetryRef.current(); return; }
    clearRetry();
    refreshRef.current();
  }, []);

  // Network came back → retry immediately instead of waiting out the backoff.
  useEffect(() => {
    const onOnline = () => { if (connectionIssue || authStatus === 'reconnecting') retryConnection(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [connectionIssue, authStatus, retryConnection]);

  const exchangeGoogleCode = useCallback(async (code, redirectUri) => {
    // Popup flow (@react-oauth/google `ux_mode: 'popup'`) uses the special
    // `postmessage` redirect_uri; the backend MUST pass the same value to Google.
    const res = await postJson('/api/auth/google', { code, redirect_uri: redirectUri || 'postmessage' });
    if (!res.ok) throw new Error(await extractError(res, 'Google sign-in failed'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [commitSession]);

  const exchangeLinkedInCode = useCallback(async (code, redirectUri) => {
    const res = await postJson('/api/auth/linkedin', { code, redirect_uri: redirectUri });
    if (!res.ok) throw new Error(await extractError(res, 'LinkedIn sign-in failed'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [commitSession]);

  const requestMagicLink = useCallback(async (email) => {
    const res = await postJson('/api/auth/magic/request', { email });
    if (!res.ok) throw new Error(await extractError(res, 'Could not send sign-in link'));
    const body = await readBody(res);
    return body ? JSON.parse(body) : {};
  }, []);

  const verifyMagicToken = useCallback(async (magicToken) => {
    const res = await postJson('/api/auth/magic/verify', { token: magicToken });
    if (!res.ok) throw new Error(await extractError(res, 'Sign-in failed'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [commitSession]);

  const requestPhoneOtp = useCallback(async (phone, countryCode, channel = 'sms') => {
    const res = await postJson('/api/auth/phone/send-otp', { phone, country_code: countryCode, channel });
    if (!res.ok) throw new Error(await extractError(res, 'Could not send the code'));
    return JSON.parse(await readBody(res));
  }, []);

  const loginWithPhone = useCallback(async (phone, countryCode, code) => {
    const res = await postJson('/api/auth/phone/verify-otp', { phone, country_code: countryCode, code });
    if (!res.ok) throw new Error(await extractError(res, 'Sign-in failed'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [commitSession]);

  // Persist a session minted by another flow (e.g. public event RSVP via OTP).
  const applySession = useCallback((data) => {
    if (!data?.token || !data?.user) return;
    commitSession(data);
  }, [commitSession]);

  // DEMO/DEV: sign in as one of the fixed test personas.
  const loginAsPersona = useCallback(async (personaId) => {
    const res = await postJson(`/api/auth/persona/${encodeURIComponent(personaId)}`);
    if (!res.ok) throw new Error(await extractError(res, 'Could not switch persona'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [commitSession]);

  // DEMO: sign in as a staff member bound to a given admin RBAC role.
  const loginAsRole = useCallback(async (roleId) => {
    const res = await postJson(`/api/auth/role-preview/${encodeURIComponent(roleId)}`);
    if (!res.ok) throw new Error(await extractError(res, 'Could not preview role'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [commitSession]);

  const logout = useCallback(() => {
    // Best-effort server-side revocation of the whole session family.
    const stored = readStored();
    if (stored?.refresh_token) {
      postJson('/api/auth/v2/logout', { refresh_token: stored.refresh_token }).catch(() => {});
    }
    hardSignOut();
  }, [hardSignOut]);

  // Merge a fresh user object into context + persisted storage (e.g. after a
  // profile save) so the whole app reflects the change without a page reload.
  const updateUser = useCallback((freshUser) => {
    if (!freshUser) return;
    setUser(freshUser);
    try { writeStored({ ...(readStored() || {}), user: freshUser }); } catch { /* ignore storage errors */ }
  }, []);

  const authedFetch = useCallback(
    async (path, opts = {}) => {
      const hasBody = opts.body != null;
      const callerHeaders = opts.headers || {};
      const hasContentType = Object.keys(callerHeaders).some((h) => h.toLowerCase() === 'content-type');
      // Skip auto Content-Type for FormData/Blob/ArrayBuffer/URLSearchParams —
      // the browser must set the correct value (multipart boundary etc.) itself.
      const b = opts.body;
      const isBrowserManagedBody = (
        (typeof FormData !== 'undefined' && b instanceof FormData) ||
        (typeof Blob !== 'undefined' && b instanceof Blob) ||
        (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) ||
        (b && b.buffer instanceof ArrayBuffer)
      );
      const doFetch = (tk) => fetch(`${API}${path}`, {
        ...opts,
        headers: {
          ...(hasBody && !hasContentType && !isBrowserManagedBody ? { 'Content-Type': 'application/json' } : {}),
          ...callerHeaders,
          ...(tk ? { Authorization: `Bearer ${tk}` } : {}),
        },
      });
      // Always send the newest token another tab may have rotated in.
      const current = readStored()?.token || token;
      let res = await doFetch(current);
      // Expired access token → one silent (cross-tab coordinated) refresh + retry.
      if (res.status === 401 && current && !path.startsWith('/api/auth/v2/')) {
        const fresh = await refreshSession(current);
        if (fresh && fresh !== current) res = await doFetch(fresh);
      }
      return res;
    },
    [token, refreshSession]
  );

  // ── Admin impersonation ("View as user") ──────────────────────────────────
  const impersonate = useCallback(async (personId, mode = 'read') => {
    const res = await postJson(`/api/admin/users/${encodeURIComponent(personId)}/impersonate`, { mode }, token);
    if (!res.ok) throw new Error(await extractError(res, 'Could not start impersonation'));
    const data = JSON.parse(await readBody(res));
    return commitSession({ token: data.token, user: data.user }, data.impersonation);
  }, [token, commitSession]);

  // ── "View as mentor" — read-only, PM-safe (backend forces mode=read) ──────
  const impersonateMentor = useCallback(async (mentorId) => {
    const res = await postJson(`/api/admin/mentors/${encodeURIComponent(mentorId)}/impersonate`, undefined, token);
    if (!res.ok) throw new Error(await extractError(res, 'Could not start the mentor view'));
    const data = JSON.parse(await readBody(res));
    return commitSession({ token: data.token, user: data.user }, data.impersonation);
  }, [token, commitSession]);

  const exitImpersonation = useCallback(async () => {
    const res = await postJson('/api/auth/impersonate/exit', undefined, token);
    if (!res.ok) throw new Error(await extractError(res, 'Could not exit impersonation'));
    return commitSession(JSON.parse(await readBody(res)));
  }, [token, commitSession]);

  // ── Parent → enter a login-less child's workspace (full, parent-initiated) ──
  const parentEnterChild = useCallback(async (childId) => {
    const res = await postJson(`/api/parent/children/${encodeURIComponent(childId)}/enter`, undefined, token);
    if (!res.ok) throw new Error(await extractError(res, 'Could not enter workspace'));
    const data = JSON.parse(await readBody(res));
    return commitSession({ token: data.token, user: data.user }, data.impersonation);
  }, [token, commitSession]);

  // ── Multi-dashboard switcher ──────────────────────────────────────────────
  const switchWorkspace = useCallback(async (roleId) => {
    const res = await authedFetch('/api/auth/switch-workspace', {
      method: 'POST', body: JSON.stringify({ role_id: roleId }),
    });
    if (!res.ok) throw new Error(await extractError(res, 'Could not switch workspace'));
    const fresh = JSON.parse(await readBody(res));
    updateUser(fresh);
    return fresh;
  }, [authedFetch, updateUser]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, impersonation, authStatus, connectionIssue, retryConnection,
      exchangeGoogleCode, exchangeLinkedInCode, requestMagicLink, verifyMagicToken, requestPhoneOtp,
      loginWithPhone, applySession, loginAsPersona, loginAsRole, impersonate, impersonateMentor,
      exitImpersonation, parentEnterChild, switchWorkspace, logout, updateUser, authedFetch,
      refreshSession, API,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
